import { defineStore } from "pinia"
import { Address } from "~/models/address"
import { ApiResponse } from "~/types/api"

export const useAddressStore = defineStore("address", () => {
	const userStore = useUserStore()

	const state = {}

	const getAddresses = async () => {
		const data = await $fetch<GetAddressesResponse>(GetAddresses.url(), {
			...useFetchConfig(),
			method: GetAddresses.method
		})
		userStore.addresses = data.data
		return data.data
	}

	const addAddress = async (formData: AddAddressRequest) => {
		const data = await $fetch<AddAddressResponse>(AddAddress.url(), {
			...useFetchConfig(),
			method: AddAddress.method,
			body: formData
		})
		userStore.addresses = [...(userStore.addresses || []), data.data]
		return data.data
	}

	const editAddress = async (formData: EditAddressRequest) => {
		const data = await $fetch<EditAddressResponse>(EditAddress.url(), {
			...useFetchConfig(),
			method: EditAddress.method,
			body: formData
		})
		userStore.addresses = userStore.addresses?.map((v) => (v.id === data.data.id ? data.data : v)) || null
		return data.data
	}

	const deleteAddress = async (id: number) => {
		const data = await $fetch<DeleteAddressResponse>(DeleteAddress.url(id), {
			...useFetchConfig(),
			method: DeleteAddress.method
		})
		userStore.addresses = userStore.addresses?.filter((v) => v.id !== id) || null
		return data.data
	}

	const reverseGeocode = async (query: ReverseGeocodeRequest) => {
		const data = await $fetch<ReverseGeocodeResponse>(ReverseGeocode.url(), {
			...useFetchConfig(),
			method: ReverseGeocode.method,
			query
		})
		const r = data.data?.[0]?.properties
		const neighbourhood = r?.neighbourhood || r?.locality || r?.county || null
		const address_country = r?.country ?? ""
		const address_region = r?.region_a ?? ""
		const address_name = r?.name ?? ""
		const address = neighbourhood ? `${address_name}, ${address_region}, ${address_country}` : null
		return {
			raw: r,
			neighbourhood,
			address_country,
			address_region,
			address_name,
			address
		}
	}

	return {
		...state,
		getAddresses,
		addAddress,
		editAddress,
		deleteAddress,
		reverseGeocode
	}
})

// address :: get addresses
export interface GetAddressesRequest {}
export interface GetAddressesResponse extends ApiResponse {
	data: Address[]
}
const GetAddresses = {
	method: "GET" as "GET",
	url: () => `addresses`
}

// address :: add address
export interface AddAddressRequest {
	city_id: number
	latitude?: number
	longitude?: number
	postal_code: string
	details: string
}
export interface AddAddressResponse extends ApiResponse {
	data: Address
}
const AddAddress = {
	method: "POST" as "POST",
	url: () => `addresses/add`
}

// address :: edit address
export interface EditAddressRequest {
	id: number
	city_id: number
	latitude?: number
	longitude?: number
	postal_code: string
	details: string
}
export interface EditAddressResponse extends ApiResponse {
	data: Address
}
const EditAddress = {
	method: "POST" as "POST",
	url: () => `addresses/edit`
}

// address :: delete address
export interface DeleteAddressRequest {}
export interface DeleteAddressResponse extends ApiResponse {
	data: null
}
const DeleteAddress = {
	method: "POST" as "POST",
	url: (id: number) => `addresses/delete/${id}`
}

// address :: reverse geocode
export interface ReverseGeocodeRequest {
	longitude: number
	latitude: number
}
export interface ReverseGeocodeResponse extends ApiResponse {
	data: any
}
const ReverseGeocode = {
	method: "GET" as "GET",
	url: () => `addresses/reverse-geocode`
}

export const useAddressApiUrl = () => ({
	GetAddresses,
	AddAddress,
	EditAddress,
	DeleteAddress,
	ReverseGeocode
})
